<template>
    <tr
        :class="[
            'control-table__row',
            'personages-card',
            {
                'is-editable': editable
            }
        ]"
    >
        <td class="personages-card__cell" style="width: 45%">
            <Spinner v-if="loading" />
            <FormField
                v-click-outside="onClickOutside"
                name="personage.name"
                class="personages-card__field"
                required
                :error="error"
            >
                <FormInput
                    ref="inputNameRes"
                    v-model="values.name"
                    :placeholder="$t('personages.list.namePlaceholder')"
                    class="form-input--compact"
                    :readonly="!editable"
                    :disabled="!editable"
                    @enter="apply"
                >
                    <template #append>
                        <button
                            v-if="editable && dirty"
                            type="button"
                            class="personages-card__field-button"
                            @click.stop="apply"
                        >
                            <IconTick />
                        </button>
                        <button
                            v-if="editable && !isNew"
                            type="button"
                            class="personages-card__field-button"
                            @click.stop="cancel"
                        >
                            <IconCross />
                        </button>
                        <button
                            v-if="!editable"
                            type="button"
                            class="personages-card__field-button personages-card__field-button--edit"
                            @click.stop="editable = true"
                        >
                            <IconPencil />
                        </button>
                    </template>
                </FormInput>
            </FormField>
        </td>
        <td class="control-table__cell" style="width: 35%">
            <FormField
                name="personage.speaker"
                class="personages-card__field"
                required
                :error="error"
            >
                <SpeakerSelect
                    v-model="values.speaker"
                    :speakers="speakers"
                    :placeholder="$t('personages.list.speaker')"
                    :player-prepend="!isNew"
                />
            </FormField>
        </td>
        <td class="control-table__cell control-table__cell--right">
            <button
                v-if="dirty && !editable"
                :class="['button', 'personages-card__button', { 'is-disabled': !canSave }]"
                :disabled="!canSave"
                @click.prevent="save"
                v-text="$t(`misc.${isNew ? 'add' : 'save'}`)"
            />
            <PersonagesDelete
                v-if="canDelete"
                :personage-id="modelValue.id"
                @deleted="onPersonageDeleted"
            />
        </td>
    </tr>
</template>

<script>
import PersonagesDelete from './PersonagesDelete';
import { createPersonage, updatePersonage } from '../../api/personages';
import { fetchSpeakers } from '../../api/speakers';
import { FormField, FormInput } from '@speechki-frontend/shared/ui/forms';
import { IconPencil, IconTick, IconCross } from '@speechki-frontend/shared/ui/icons';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';
import { mapState, mapActions } from 'vuex';

export default {
    name: 'PersonagesCard',
    components: {
        FormField,
        FormInput,
        IconPencil,
        IconTick,
        IconCross,
        SpeakerSelect,
        Spinner,
        PersonagesDelete
    },
    directives: {
        clickOutside
    },
    props: {
        modelValue: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:modelValue', 'personageUpdate', 'personageDelete'],
    data() {
        return {
            loading: false,
            error: undefined,
            editable: !('id' in this.modelValue),
            values: {
                name: this.modelValue.name || null,
                speaker: this.modelValue.speaker.id || 0
            },
            speakers: []
        };
    },
    computed: {
        ...mapState({
            book: ({ book }) => book.book
        }),
        valid() {
            return Object.keys(this.values).every(k => !!this.values[k]);
        },
        dirty() {
            return [
                this.values.name != this.modelValue.name,
                this.values.speaker != this.modelValue.speaker.id
            ].some(v => v);
        },
        isNew() {
            return !('id' in this.modelValue);
        },
        canSave() {
            if (this.isNew) return this.valid && !this.loading;
            return this.dirty && this.valid && !this.loading && !this.editable;
        },
        canDelete() {
            return !this.dirty && !this.editable && !this.isNew;
        }
    },
    watch: {
        editable: {
            async handler(editable) {
                if (!editable) return;
                await this.$nextTick();
                const { inputNameRes = null } = this.$refs;
                if (inputNameRes) inputNameRes.focus();
            },
            immediate: true
        }
    },
    async created() {
        this.loading = true;

        try {
            await this.fetchSpeakers();
        } catch (err) {
            console.log(err);
        } finally {
            this.loading = false;
        }
    },
    methods: {
        ...mapActions(['pushToast']),
        apply() {
            this.editable = false;
        },
        cancel() {
            this.editable = false;
            this.values.name = this.modelValue.name;
        },
        save() {
            this.onClickOutside();

            if (!this.canSave) {
                console.log('[PersonagesCard]: canSave', this.canSave);
                return false;
            }

            const method = this.isNew ? 'createPersonage' : 'updatePersonage';
            const data = { ...this.modelValue, ...this.values };

            return this[method](data);
        },
        onClickOutside() {
            if ((!this.editable && !this.dirty) || (this.isNew && !this.dirty)) {
                return;
            }
            this.apply();
        },
        async createPersonage(item) {
            this.loading = true;

            try {
                const book = this.book.id;
                await createPersonage({ ...item, book });
                this.$emit('update:modelValue', item);
            } catch (error) {
                this.handleError(error);
            } finally {
                this.loading = false;
            }
        },
        async updatePersonage(item) {
            this.loading = true;

            try {
                await updatePersonage(item.id, item);
                this.$emit('personageUpdate', item);
            } catch (error) {
                this.handleError(error);
            } finally {
                this.loading = false;
            }
        },
        onPersonageDeleted(event) {
            this.$emit('personageDelete', event);
        },
        handleError(error) {
            this.error = error;
            this.pushToast({
                id: Date.now(),
                type: 'error'
            });
            console.log(error);
        },
        async fetchSpeakers() {
            this.speakers = await fetchSpeakers({
                limits: this.book.limit.id || this.book.limit,
                page_size: 5000,
                book_language: this.book.book_language
            });
        }
    }
};
</script>

<style lang="scss" src="./PersonagesCard.scss"></style>
